import api from 'api';
import { useAppSelector, useAppDispatch } from 'hooks/useAppDispatchSelector';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import DatePickerComponent from 'components/common/date-picker';
import { useTranslation } from 'react-i18next';
import { useState, useContext, useEffect } from 'react';
import { setModal } from 'store/features/modelSlice';
import { completeAlert, editAlertKmOrDate, IEditAlertRequest } from 'store/features/alertsSlice';
import { initModal, onlyNumbers } from 'helpers/utils';
import { AppContext } from 'context/app-context';
import InputField from 'components/common/input-field';
import { ReactComponent as RefreshIcon } from 'assets/icon/refresh.svg';
import toast from 'react-hot-toast';

const EditAlert = () => {
  const { vehicleAlertDetail, generatedAlert } = useAppSelector(state => state.alerts);
  const alertDetail = vehicleAlertDetail || generatedAlert;

  const {
    modal: {
      content: { callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const dispatch = useAppDispatch();
  const { setLoader } = useContext(AppContext);
  const { t } = useTranslation();

  // State for editing fields
  const [alertKm, setAlertKm] = useState(alertDetail?.alert_update_km || '');
  const [alertDate, setAlertDate] = useState(alertDetail?.alert_update_date || '');

  // Function to handle alert editing
  const handleEditAlert = async () => {
    try {
      setLoader(true);
      const alertData: IEditAlertRequest = {};
      if (alertKm) {
        alertData.alert_update_km = alertKm;
      }
      if (alertDate) {
        alertData.alert_update_date = alertDate;
      }

      // Call Redux action to update the alert
      await dispatch(editAlertKmOrDate({ alert_id: alertDetail?._id || '', request: alertData }));

      if (alertDetail?.status !== 'completed') {
        await dispatch(completeAlert({ alert_id: alertDetail?._id || '' }))
          .then(() => {
            toast.success(t('alerts.alertCompleteSuccess'));
            callBackFun && callBackFun();
          })
          .catch(() => setLoader(false));
      }

      if (alertDetail?.status === 'completed') {
        toast.success(t('alerts.alertEditSuccess'));
      }
      dispatch(setModal(initModal));
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };

  const handleUpdateKm = async () => {
    try {
      setLoader(true);
      const { data: responseData } = await api.get(
        `/admin/vehicle/force-update/km/${alertDetail?.data.vehicle_id}`,
      );
      setAlertKm(responseData.data);
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-full max-w-[450px] min-w-[400px]">
      <div className="flex flex-col w-full gap-y-3 overflow-y-auto max-h-screen">
        <div className="flex items-center w-full gap-x-1">
          <label className="text-xl font-semibold text-black2 whitespace-nowrap overflow-hidden flex-1 text-ellipsis">
            {alertDetail?.title}
          </label>
        </div>
        <div className="flex flex-col gap-y-3 mt-3">
          <div className="flex flex-col gap-y-2">
            <label className="text-sm font-medium text-grey9">{t('alerts.alertUpdateDate')}</label>
            <DatePickerComponent
              selected={String(alertDate)}
              onChange={date => setAlertDate(String(date))}
            />
          </div>
          <div className="flex flex-col gap-y-2">
            <div className="flex items-center gap-y-2">
              <label className="font-medium text-base text-grey9">{t('alerts.kmVal')}</label>
              <RefreshIcon
                className="cursor-pointer ml-5"
                height={16}
                width={16}
                color="#337B9E"
                onClick={handleUpdateKm}
              />
            </div>
            <div className="flex flex-col gap-y-2">
              {/* <label className="text-sm font-medium text-grey9">
                {t('alerts.alertTriggeredKm')}
              </label> */}
              <InputField
                placeholder={t('ENTER_KM')}
                value={typeof alertKm === 'undefined' || alertKm === '' ? '' : alertKm || ''}
                onChange={val => setAlertKm(onlyNumbers.test(val) || !val.trim() ? val : alertKm)}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center w-full gap-x-4 mt-5">
          <PrimaryDarkButton
            label={
              alertDetail?.status !== 'completed'
                ? t('alerts.markAsComplete')
                : t('alerts.saveChanges')
            }
            onClick={handleEditAlert}
            width="w-1/2"
          />
          <DarkButton
            label={t('CANCEL')}
            width="w-1/2"
            onClick={() => dispatch(setModal(initModal))}
          />
        </div>
      </div>
    </div>
  );
};

export default EditAlert;
